import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { LocalizeContextProps, withLocalize } from "react-localize-redux";
import { Field, Spinner } from "@fluentui/react-components";
import { DetailedKeyword, KeywordListType } from "../../../models/ProjectKeywordsResponse";
import { Keyword } from "../../../models/Keyword";
import { IProjectsService } from "../../../services/NewformaApi/IProjectsService";
import { IProjectData } from "@src/models/shared/IProjectData";
import { ProjectHelper } from "../../../helpers/ProjectHelper";
import { Log, Logger } from "../../../services/Logger";
import NewformaDropdown from "../../_base/NewformaDropdown";

interface PurposeDropdownFluent9Props extends LocalizeContextProps {
    selectedProject: IProjectData | null | undefined;
    projectsService: IProjectsService;
    logger: Logger;
    disabled?: boolean;
    showPurposeField: boolean;
    selectedPurpose: Keyword | null;
    onPurposeChange: (purpose: Keyword | null) => void;
    showValidationError?: boolean;
    testid?: string;
}

const PurposeDropdownFluent9: React.FC<PurposeDropdownFluent9Props> = ({
    selectedProject,
    projectsService,
    logger,
    disabled,
    showPurposeField,
    selectedPurpose,
    onPurposeChange,
    showValidationError,
    translate,
    testid = "purpose-dropdown",
}) => {
    const [purposes, setPurposes] = useState<DetailedKeyword[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const latestRequestRef = useRef(0);

    useEffect(() => {
        if (!selectedProject) {
            latestRequestRef.current += 1;
            setPurposes([]);
            return;
        }

        const loadPurposes = async () => {
            const requestId = ++latestRequestRef.current;
            setIsLoading(true);
            Log.info(`Loading purposes for project: ${selectedProject.name}`);
            try {
                const response = await projectsService.getProjectKeywords(
                    selectedProject.nrn,
                    KeywordListType.TransmittalPurposes
                );
                if (requestId !== latestRequestRef.current) return;
                setPurposes(ProjectHelper.sortKeywords(response));
            } catch (error) {
                if (requestId !== latestRequestRef.current) return;
                logger.error("Failed to load purposes", error);
                setPurposes([]);
            } finally {
                if (requestId !== latestRequestRef.current) return;
                setIsLoading(false);
            }
        };

        void loadPurposes();

        return () => {
            latestRequestRef.current += 1;
        };
    }, [selectedProject, projectsService, logger]);

    const purposeMap = useMemo(() => new Map(purposes.map((p) => [p.name, p])), [purposes]);

    const dropdownOptions = useMemo(() => purposes.map((p) => ({ key: p.name, text: p.name })), [purposes]);

    const handleOptionSelect = useCallback(
        (_event: unknown, data: { optionValue?: string }) => {
            if (!data.optionValue) {
                onPurposeChange(null);
                return;
            }
            const selected = purposeMap.get(data.optionValue);
            if (selected) {
                onPurposeChange({ name: selected.name, type: selected.type });
            }
        },
        [purposeMap, onPurposeChange]
    );

    if (!showPurposeField || (!isLoading && purposes.length === 0)) {
        return null;
    }

    const isDisabled = disabled || !selectedProject || isLoading;

    const placeholder = isLoading
        ? (translate("ACTION_ITEM.LOADING_KEYWORDS") as string)
        : (translate("APP.FILE_TRANSFER.PURPOSE.PLACEHOLDER") as string);

    const validationMessage = showValidationError ? (translate("SHARED.FIELD.REQUIRED_ERROR") as string) : undefined;

    return (
        <Field
            label={translate("APP.FILE_TRANSFER.PURPOSE.LABEL") as string}
            required={true}
            validationMessage={validationMessage}
            validationState={showValidationError ? "error" : "none"}
            data-testid={`${testid}-field`}
        >
            {isLoading ? (
                <Spinner size="tiny" />
            ) : (
                <NewformaDropdown
                    data-testid={testid}
                    placeholder={placeholder}
                    disabled={isDisabled}
                    value={selectedPurpose?.name ?? ""}
                    selectedOptions={selectedPurpose ? [selectedPurpose.name] : []}
                    onOptionSelect={handleOptionSelect}
                    options={dropdownOptions}
                />
            )}
        </Field>
    );
};

export default withLocalize(PurposeDropdownFluent9);
